import React, { useState, useEffect } from 'react';
import { Search, Bot, Loader2, AlertCircle, FileText, Database, Layers } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ExtractResult {
  id: string;
  url: string;
  texto_extraido: string | null;
  chunk: boolean;
  embedding: boolean;
  vectorizado: boolean;
  error?: string;
  created_at: string;
  asistente?: {
    nombre: string;
  };
}

const chunkText = (text: string, chunkSize: number = 1000, overlap: number = 200): string[] => {
  const chunks: string[] = [];
  let start = 0;
  while (start < text.length) {
    const end = Math.min(start + chunkSize, text.length);
    const chunk = text.slice(start, end);
    chunks.push(chunk.trim());
    start += chunkSize - overlap;
  }
  return chunks;
};

const getEmbedding = async (text: string, apiKey: string): Promise<number[]> => {
  const response = await fetch('https://api.openai.com/v1/embeddings', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      input: text,
      model: 'text-embedding-3-small'
    })
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error?.message || 'Error generando embedding');
  }
  return data.data[0].embedding;
};

export default function TavilyVectorization() {
  const [results, setResults] = useState<ExtractResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showProcessed, setShowProcessed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data, error } = await supabase
        .from('tavily_extract_results')
        .select(`
          *,
          asistente:asistentes(nombre)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResults(data || []);
    } catch (error: any) {
      console.error('Error fetching results:', error);
      setError('Error al cargar los resultados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChunk = async () => {
    try {
      setIsProcessing(true);
      setError('');
      
      const results = await supabase
        .from('tavily_extract_results')
        .select('*')
        .eq('chunk', false)
        .is('error', null);

      if (results.error) throw results.error;

      for (const result of results.data) {
        if (!result.texto_extraido) continue;

        const chunks = chunkText(result.texto_extraido);
        
        // Mark as chunked
        await supabase
          .from('tavily_extract_results')
          .update({ chunk: true })
          .eq('id', result.id);
      }

      setSuccess('Proceso de chunking completado');
      fetchResults();
    } catch (error: any) {
      console.error('Error in chunking:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEmbedding = async () => {
    try {
      setIsProcessing(true);
      setError('');

      const results = await supabase
        .from('tavily_extract_results')
        .select('*')
        .eq('chunk', true)
        .eq('embedding', false)
        .is('error', null);

      if (results.error) throw results.error;

      // Get OpenAI API key from config
      const { data: apiConfig } = await supabase
        .from('api_configuraciones')
        .select('api_key')
        .eq('estado', 'activo')
        .single();

      if (!apiConfig) throw new Error('No se encontró configuración de API activa');

      for (const result of results.data) {
        if (!result.texto_extraido) continue;

        const chunks = chunkText(result.texto_extraido);
        
        for (let i = 0; i < chunks.length; i++) {
          try {
            const embedding = await getEmbedding(chunks[i], apiConfig.api_key);
            
            await supabase
              .from('documentos_vectorizados')
              .insert({
                documento_id: result.id,
                asistente_id: result.asistente_id,
                chunk_texto: chunks[i],
                embedding: embedding,
                orden: i
              });
          } catch (error) {
            console.error(`Error generating embedding for chunk ${i}:`, error);
          }
        }

        // Mark as embedded
        await supabase
          .from('tavily_extract_results')
          .update({ embedding: true })
          .eq('id', result.id);
      }

      setSuccess('Proceso de embedding completado');
      fetchResults();
    } catch (error: any) {
      console.error('Error in embedding:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVectorize = async () => {
    try {
      setIsProcessing(true);
      setError('');

      const results = await supabase
        .from('tavily_extract_results')
        .select('*')
        .eq('embedding', true)
        .eq('vectorizado', false)
        .is('error', null);

      if (results.error) throw results.error;

      for (const result of results.data) {
        // Mark as vectorized since embeddings are already stored
        await supabase
          .from('tavily_extract_results')
          .update({ vectorizado: true })
          .eq('id', result.id);
      }

      setSuccess('Proceso de vectorización completado');
      fetchResults();
    } catch (error: any) {
      console.error('Error in vectorization:', error);
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const filteredResults = results.filter(result => {
    if (!showProcessed && (result.chunk || result.embedding || result.vectorizado)) {
      return false;
    }

    if (searchTerm) {
      return (
        result.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.asistente?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        result.texto_extraido?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Vectorización de Textos</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={handleChunk}
            disabled={isProcessing || !results.some(r => !r.chunk)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center space-x-2"
          >
            <Layers className="w-5 h-5" />
            <span>Chunk</span>
          </button>
          <button
            onClick={handleEmbedding}
            disabled={isProcessing || !results.some(r => r.chunk && !r.embedding)}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center space-x-2"
          >
            <Bot className="w-5 h-5" />
            <span>Embedding</span>
          </button>
          <button
            onClick={handleVectorize}
            disabled={isProcessing || !results.some(r => r.embedding && !r.vectorizado)}
            className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 disabled:opacity-50 flex items-center space-x-2"
          >
            <Database className="w-5 h-5" />
            <span>Vectorizar</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showProcessed}
                onChange={(e) => setShowProcessed(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Mostrar procesados</span>
            </label>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Chunk</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span>Embedding</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
              <span>Vectorizado</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {filteredResults.map((result) => (
            <div key={result.id} className="border rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    {result.url}
                  </a>
                  <div className="text-sm text-gray-500 mt-1">
                    Asistente: {result.asistente?.nombre}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    result.chunk ? 'bg-green-500' : 'bg-gray-300'
                  }`}></div>
                  <div className={`w-3 h-3 rounded-full ${
                    result.embedding ? 'bg-blue-500' : 'bg-gray-300'
                  }`}></div>
                  <div className={`w-3 h-3 rounded-full ${
                    result.vectorizado ? 'bg-purple-500' : 'bg-gray-300'
                  }`}></div>
                </div>
              </div>

              {result.error ? (
                <div className="text-red-600 text-sm mt-2">
                  Error: {result.error}
                </div>
              ) : result.texto_extraido ? (
                <div className="mt-2 text-gray-600 text-sm">
                  <div className="max-h-32 overflow-y-auto">
                    {result.texto_extraido}
                  </div>
                </div>
              ) : (
                <div className="text-gray-500 text-sm mt-2 italic">
                  Sin texto extraído
                </div>
              )}

              <div className="text-xs text-gray-400 mt-2">
                {new Date(result.created_at).toLocaleString()}
              </div>
            </div>
          ))}

          {filteredResults.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No se encontraron resultados</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}